// Instrument sampler (§4.11): loads one sprite per sound id and plays notes
// from it with AudioBufferSourceNode.start(when, offset, dur).
//
// Loading order per sound id:
//  1. recorded sprite files: `<base>/<id>.json` (map) + `<base>/<id>.ogg|mp3`.
//     `<base>/manifest.json` (`{ "sounds": ["gtr_nylon", …] }`) lists the ids
//     that have files, so absent sounds cost no requests; without a manifest
//     every id is probed.
//  2. otherwise the sprite is synthesized (in a worker when available, else on
//     the main thread in slices) and cached for the session.

import { midiToHz } from './dsp';
import { harmonicMidi, nearestSample, parseSpriteMap, type Pcm, type SpriteData, type SpriteMap } from './sprite';
import { SYNTH_SAMPLE_RATE, renderSoundAsync, soundProfile, type SoundKind, type SoundProfile } from './synth';

export interface LoadedSound {
  id: string;
  profile: SoundProfile;
  map: SpriteMap;
  exact: boolean;
  source: 'synth' | 'file';
  /** Synthesized PCM (or null for a recorded file still to be decoded). */
  pcm: { sampleRate: number; data: Pcm } | null;
  /** Encoded audio file bytes (recorded sprites). */
  encoded: ArrayBuffer | null;
}

export interface SamplerOptions {
  /** Folder holding `<id>.json` + audio, default `<BASE_URL>sounds/`. */
  baseUrl?: string;
  /** fetch implementation; `null` disables recorded sprites entirely. */
  fetch?: typeof fetch | null;
  /** Family of sound ids unknown to the built-in synth (e.g. from @simplenotation/core SOUNDS). */
  kindOf?: (id: string) => SoundKind | undefined;
  /** Render in a Web Worker when possible (default true). */
  worker?: boolean;
}

/** Note parameters for one voice. */
export interface VoiceParams {
  /** Sounding MIDI (before harmonic transposition). */
  midi: number;
  /** Context time of the onset. */
  when: number;
  /** Seconds until note-off. */
  duration: number;
  /** 0…1 */
  velocity: number;
  dead?: boolean;
  palmMute?: boolean;
  harmonic?: 'natural' | 'artificial';
  fret?: number;
  /** Bend curve: t in 0…1 of the note, semitones. */
  bend?: readonly { t: number; semis: number }[];
  vibrato?: boolean;
  /** Hammer-on/pull-off/legato-slide target: softer attack. */
  legato?: boolean;
  /** Extra gain factor. */
  gain?: number;
}

export interface Voice {
  /** Onset (context time). */
  readonly start: number;
  /** Time after which the voice is silent and stopped. */
  end: number;
  /** Fade out from `at` over `fade` seconds and stop. */
  release(at: number, fade?: number): void;
  /** Drop a voice that has not started yet. */
  cancel(): void;
}

/** Overall per-voice level (leaves headroom for chords and several tracks). */
const VOICE_GAIN = 0.32;

function defaultBaseUrl(): string {
  const env = (import.meta as unknown as { env?: { BASE_URL?: string } }).env;
  const base = env?.BASE_URL ?? '/';
  return (base.endsWith('/') ? base : base + '/') + 'sounds/';
}

/** Synthesis in a module worker, with request ids; resolves null on failure. */
class SynthWorkerClient {
  private worker: Worker | null = null;
  private broken = false;
  private seq = 0;
  private pending = new Map<number, (sp: SpriteData | null) => void>();

  render(id: string, kind: SoundKind | undefined, sampleRate: number): Promise<SpriteData | null> {
    if (this.broken) return Promise.resolve(null);
    try {
      if (!this.worker) {
        if (typeof Worker === 'undefined') {
          this.broken = true;
          return Promise.resolve(null);
        }
        this.worker = new Worker(new URL('./synth.worker.ts', import.meta.url), { type: 'module' });
        this.worker.onmessage = (e: MessageEvent<{ req: number; sprite?: SpriteData; error?: string }>) => {
          const cb = this.pending.get(e.data.req);
          this.pending.delete(e.data.req);
          cb?.(e.data.sprite ?? null);
        };
        this.worker.onerror = () => this.fail();
      }
    } catch {
      this.fail();
      return Promise.resolve(null);
    }
    const req = ++this.seq;
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        if (this.pending.delete(req)) resolve(null);
      }, 20000);
      this.pending.set(req, (sp) => {
        clearTimeout(timer);
        resolve(sp);
      });
      this.worker!.postMessage({ req, id, kind, sampleRate });
    });
  }

  private fail(): void {
    this.broken = true;
    this.worker?.terminate();
    this.worker = null;
    for (const cb of this.pending.values()) cb(null);
    this.pending.clear();
  }

  dispose(): void {
    this.worker?.terminate();
    this.worker = null;
  }
}

export class Sampler {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch | null;
  private readonly kindOf: (id: string) => SoundKind | undefined;
  private readonly useWorker: boolean;
  private readonly sounds = new Map<string, Promise<LoadedSound>>();
  private readonly ready = new Map<string, LoadedSound>();
  private readonly buffers = new WeakMap<BaseAudioContext, Map<string, Promise<AudioBuffer | null>>>();
  private readonly decoded = new WeakMap<BaseAudioContext, Map<string, AudioBuffer>>();
  private readonly harmonicWaves = new WeakMap<BaseAudioContext, PeriodicWave>();
  private manifest: Promise<Set<string> | null> | null = null;
  private workerClient = new SynthWorkerClient();

  constructor(opts: SamplerOptions = {}) {
    this.baseUrl = opts.baseUrl ?? defaultBaseUrl();
    this.fetchImpl = opts.fetch === undefined ? (typeof fetch === 'function' ? fetch.bind(globalThis) : null) : opts.fetch;
    this.kindOf = opts.kindOf ?? (() => undefined);
    this.useWorker = opts.worker ?? true;
  }

  profile(id: string): SoundProfile {
    return this.ready.get(id)?.profile ?? soundProfile(id, this.kindOf(id));
  }

  isLoaded(id: string): boolean {
    return this.ready.has(id);
  }

  /** Load (fetch or synthesize) a sound; cached per id. */
  load(id: string): Promise<LoadedSound> {
    let p = this.sounds.get(id);
    if (!p) {
      p = this.loadUncached(id).then((s) => {
        this.ready.set(id, s);
        return s;
      });
      p.catch(() => this.sounds.delete(id));
      this.sounds.set(id, p);
    }
    return p;
  }

  /** Load sounds and create their AudioBuffers on a context. */
  async ensure(ctx: BaseAudioContext, ids: Iterable<string>): Promise<void> {
    await Promise.all([...new Set(ids)].map((id) => this.bufferAsync(ctx, id)));
  }

  /** AudioBuffer of a loaded sound on a context, or null if not ready yet. */
  buffer(ctx: BaseAudioContext, id: string): AudioBuffer | null {
    return this.decoded.get(ctx)?.get(id) ?? null;
  }

  bufferAsync(ctx: BaseAudioContext, id: string): Promise<AudioBuffer | null> {
    let perCtx = this.buffers.get(ctx);
    if (!perCtx) this.buffers.set(ctx, (perCtx = new Map()));
    let p = perCtx.get(id);
    if (!p) {
      p = this.load(id).then(async (s) => {
        let buf: AudioBuffer | null = null;
        if (s.pcm) {
          buf = ctx.createBuffer(1, s.pcm.data.length, s.pcm.sampleRate);
          buf.copyToChannel(s.pcm.data, 0);
        } else if (s.encoded) {
          try {
            buf = await ctx.decodeAudioData(s.encoded.slice(0));
          } catch {
            // Undecodable file: fall back to synthesis.
            const synth = await this.synthesize(id);
            Object.assign(s, { map: synth.map, exact: !!synth.exact, source: 'synth', encoded: null, pcm: { sampleRate: synth.sampleRate, data: synth.data } });
            buf = ctx.createBuffer(1, synth.data.length, synth.sampleRate);
            buf.copyToChannel(synth.data, 0);
          }
        }
        if (buf) {
          let d = this.decoded.get(ctx);
          if (!d) this.decoded.set(ctx, (d = new Map()));
          d.set(id, buf);
        }
        return buf;
      });
      p.catch(() => perCtx!.delete(id));
      perCtx.set(id, p);
    }
    return p;
  }

  private async loadUncached(id: string): Promise<LoadedSound> {
    const profile = soundProfile(id, this.kindOf(id));
    const file = await this.loadFile(id).catch(() => null);
    if (file) return { id, profile, map: file.map, exact: profile.kind === 'drums', source: 'file', pcm: null, encoded: file.bytes };
    const sp = await this.synthesize(id);
    return { id, profile, map: sp.map, exact: !!sp.exact, source: 'synth', pcm: { sampleRate: sp.sampleRate, data: sp.data }, encoded: null };
  }

  private async synthesize(id: string): Promise<SpriteData> {
    const kind = this.kindOf(id);
    if (this.useWorker) {
      const sp = await this.workerClient.render(id, kind, SYNTH_SAMPLE_RATE);
      if (sp) return sp;
    }
    return renderSoundAsync(id, SYNTH_SAMPLE_RATE, kind);
  }

  private async getManifest(): Promise<Set<string> | null> {
    if (!this.fetchImpl) return new Set();
    this.manifest ??= (async () => {
      try {
        const res = await this.fetchImpl!(this.baseUrl + 'manifest.json');
        if (!res.ok || !(res.headers.get('content-type') ?? '').includes('json')) return null;
        const j = (await res.json()) as { sounds?: unknown };
        return Array.isArray(j.sounds) ? new Set(j.sounds.filter((x): x is string => typeof x === 'string')) : null;
      } catch {
        return null;
      }
    })();
    return this.manifest;
  }

  private async loadFile(id: string): Promise<{ map: SpriteMap; bytes: ArrayBuffer } | null> {
    const f = this.fetchImpl;
    if (!f) return null;
    const manifest = await this.getManifest();
    if (manifest && !manifest.has(id)) return null;
    const res = await f(this.baseUrl + encodeURIComponent(id) + '.json');
    if (!res.ok) return null;
    let map: SpriteMap | null = null;
    try {
      map = parseSpriteMap(await res.json());
    } catch {
      return null; // e.g. an SPA fallback page
    }
    if (!map) return null;
    for (const ext of audioExtensions()) {
      const a = await f(this.baseUrl + encodeURIComponent(id) + '.' + ext).catch(() => null);
      if (a && a.ok && !(a.headers.get('content-type') ?? '').includes('text/html')) return { map, bytes: await a.arrayBuffer() };
    }
    return null;
  }

  private harmonicWave(ctx: BaseAudioContext): PeriodicWave {
    let w = this.harmonicWaves.get(ctx);
    if (!w) {
      w = ctx.createPeriodicWave(new Float32Array([0, 0, 0, 0]), new Float32Array([0, 1, 0.12, 0.04]));
      this.harmonicWaves.set(ctx, w);
    }
    return w;
  }

  /**
   * Start a voice of a loaded sound. Returns null if the sound's buffer is not
   * ready on this context.
   */
  startVoice(ctx: BaseAudioContext, destination: AudioNode, id: string, p: VoiceParams): Voice | null {
    const sound = this.ready.get(id);
    const buffer = this.buffer(ctx, id);
    if (!sound || (!buffer && !p.harmonic)) return null;
    const prof = sound.profile;
    const now = ctx.currentTime;
    const when = Math.max(p.when, now);
    const vel = Math.max(0.05, Math.min(1, p.velocity));
    const amp = (0.12 + 0.88 * Math.pow(vel, 1.6)) * prof.gain * VOICE_GAIN * (p.gain ?? 1) * (p.legato ? 0.7 : 1);
    let dur = Math.max(0.02, p.duration - (when - p.when));
    let release = prof.release;
    if (prof.minRing) dur = Math.max(dur, prof.minRing);
    if (p.dead) {
      dur = 0.025;
      release = 0.03;
    } else if (p.palmMute) release = Math.min(release, 0.05);

    const nodes: AudioNode[] = [];
    const g = ctx.createGain();
    nodes.push(g);
    const gp = g.gain;
    gp.setValueAtTime(0, when);
    gp.linearRampToValueAtTime(amp, when + 0.003);

    let src: AudioScheduledSourceNode;
    let detuneParam: AudioParam | null = null;
    let slotEnd = Infinity;
    const lfoNodes: AudioScheduledSourceNode[] = [];

    if (p.harmonic) {
      const osc = ctx.createOscillator();
      osc.setPeriodicWave(this.harmonicWave(ctx));
      const f = midiToHz(harmonicMidi(p.midi, p.harmonic, p.fret));
      osc.frequency.setValueAtTime(f, when);
      if (p.bend?.length) applyBend(osc.frequency, f, when, dur, p.bend);
      detuneParam = osc.detune;
      // Bell-like decay of a pure partial.
      gp.setTargetAtTime(0, when + 0.01, 0.5);
      osc.start(when);
      src = osc;
    } else {
      const sel = nearestSample(sound.map, p.midi, sound.exact);
      if (!sel || !buffer) return null;
      const bs = ctx.createBufferSource();
      bs.buffer = buffer;
      bs.playbackRate.setValueAtTime(sel.rate, when);
      if (p.bend?.length) applyBend(bs.playbackRate, sel.rate, when, dur, p.bend);
      detuneParam = bs.detune ?? null;
      if (sel.loopStart !== undefined && sel.loopEnd !== undefined && !p.dead) {
        bs.loop = true;
        bs.loopStart = sel.loopStart;
        bs.loopEnd = sel.loopEnd;
        bs.start(when, sel.offset);
      } else {
        bs.start(when, sel.offset, sel.dur);
        // Bends up consume the sample faster; a conservative end estimate.
        const maxRate = sel.rate * Math.pow(2, Math.max(0, ...(p.bend ?? []).map((b) => b.semis)) / 12);
        slotEnd = when + sel.dur / maxRate;
      }
      if (prof.oneShot) dur = sel.dur / sel.rate;
      src = bs;
    }

    if (p.palmMute && !p.dead) gp.setTargetAtTime(0, when + 0.015, 0.09);

    // Optional tone filter: dead notes, palm mutes, soft plucks/piano.
    let head: AudioNode = g;
    const soft = (prof.kind === 'pluck' || prof.kind === 'piano') && vel < 0.6;
    if (p.dead || p.palmMute || soft) {
      const f = ctx.createBiquadFilter();
      f.type = 'lowpass';
      const f0 = midiToHz(p.midi);
      f.frequency.value = p.dead ? 1400 : p.palmMute ? Math.min(2600, Math.max(500, f0 * 4)) : 2000 + 16000 * vel * vel;
      f.Q.value = 0.6;
      f.connect(g);
      head = f;
      nodes.push(f);
    }
    src.connect(head);
    g.connect(destination);

    if (p.vibrato && detuneParam) {
      const lfo = ctx.createOscillator();
      lfo.frequency.value = 5.5;
      const depth = ctx.createGain();
      depth.gain.value = 0;
      depth.gain.setValueAtTime(0, when);
      depth.gain.linearRampToValueAtTime(28, when + Math.min(0.15, dur * 0.3));
      lfo.connect(depth).connect(detuneParam);
      lfo.start(when);
      lfoNodes.push(lfo);
      nodes.push(depth);
    }

    const off = when + dur;
    gp.setTargetAtTime(0, off, release / 3);
    let stopAt = Math.min(off + release * 2.5 + 0.01, slotEnd + 0.01);
    if (stopAt <= when) stopAt = when + 0.05;
    src.stop(stopAt);
    for (const l of lfoNodes) l.stop(stopAt);

    let done = false;
    const cleanup = () => {
      if (done) return;
      done = true;
      try {
        src.disconnect();
        for (const l of lfoNodes) l.disconnect();
        for (const n of nodes) n.disconnect();
      } catch {
        /* already disconnected */
      }
    };
    src.onended = cleanup;

    const voice: Voice = {
      start: when,
      end: stopAt,
      release(at: number, fade = 0.03) {
        if (done) return;
        const t = Math.max(at, ctx.currentTime);
        if (t >= voice.end) return;
        if (t < when) {
          voice.cancel();
          return;
        }
        holdAt(gp, t);
        gp.setTargetAtTime(0, t, fade / 3);
        const s = t + fade * 2 + 0.01;
        try {
          src.stop(s);
          for (const l of lfoNodes) l.stop(s);
        } catch {
          /* already stopped */
        }
        voice.end = Math.min(voice.end, s);
      },
      cancel() {
        if (done) return;
        try {
          src.stop();
          for (const l of lfoNodes) l.stop();
        } catch {
          /* not started */
        }
        voice.end = ctx.currentTime;
        cleanup();
      },
    };
    return voice;
  }

  dispose(): void {
    this.workerClient.dispose();
  }
}

/** Hold an AudioParam at its current value from time t (cancelling later automation). */
function holdAt(param: AudioParam, t: number): void {
  const p = param as AudioParam & { cancelAndHoldAtTime?: (t: number) => AudioParam };
  if (typeof p.cancelAndHoldAtTime === 'function') p.cancelAndHoldAtTime(t);
  else {
    const v = param.value;
    param.cancelScheduledValues(t);
    param.setValueAtTime(v, t);
  }
}

/** Bend automation: rate × 2^(semis/12) following the points over the note. */
export function applyBend(param: AudioParam, base: number, when: number, dur: number, points: readonly { t: number; semis: number }[]): void {
  const pts = [...points].sort((a, b) => a.t - b.t);
  const first = pts[0];
  param.setValueAtTime(base * Math.pow(2, (first.t <= 0 ? first.semis : 0) / 12), when);
  for (const p of pts) {
    const t = when + Math.max(0, Math.min(1, p.t)) * dur;
    param.linearRampToValueAtTime(base * Math.pow(2, p.semis / 12), Math.max(when + 0.001, t));
  }
}

/** Audio formats to try for recorded sprites, best first. */
function audioExtensions(): string[] {
  try {
    if (typeof Audio !== 'undefined') {
      const a = new Audio();
      if (!a.canPlayType('audio/ogg; codecs="vorbis"')) return ['mp3', 'ogg'];
    }
  } catch {
    /* no media elements */
  }
  return ['ogg', 'mp3'];
}

